import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from './Navbar';
import Footer from './Footer';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Privacy Policy | Clarendon Family Medicine</title>
        <meta name="description" content="Privacy policy for Clarendon Family Medicine, including our HIPAA practices and SMS patient communication program." />
        <meta property="og:title" content="Privacy Policy | Clarendon Family Medicine" />
        <meta property="og:url" content="https://www.clarendon.clinic/privacypolicy" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.clarendon.clinic/privacypolicy" />
      </Helmet>
      <Navbar />
      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          {/* Header */}
          <div className="mb-10 pb-8 border-b border-sky-100">
            <p className="text-sky-500 font-semibold text-sm uppercase tracking-widest mb-3">Legal</p>
            <h1
              data-testid="privacy-policy-title"
              className="text-4xl sm:text-5xl font-bold text-sky-900 mb-4"
              style={{fontFamily: 'Outfit, sans-serif'}}
            >
              Privacy Policy
            </h1>
            <p className="text-sky-600 text-sm">
              <strong>Clarendon Family Medicine</strong> &nbsp;|&nbsp; Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Body */}
          <div className="prose-sky space-y-8 text-sky-800 text-base leading-relaxed" data-testid="privacy-policy-content">

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>1. Introduction</h2>
              <p>
                Clarendon Family Medicine ("we," "us," or "our"), located at One Medical Center Drive, Clarendon, TX 79226, is committed to protecting your privacy and the confidentiality of your personal and health information. This Privacy Policy describes how we collect, use, store, and protect information when you interact with our services, including our website and any SMS-based patient communication programs.
              </p>
              <p className="mt-3">
                By using our website or enrolling in our SMS communication program, you agree to the practices described in this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>2. Information We Collect</h2>
              <p>We may collect the following types of information:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2 text-sky-700">
                <li><strong>Personal Identification Information:</strong> Name, date of birth, address, phone number, and similar contact details provided when you register as a patient or contact our clinic.</li>
                <li><strong>Health Information:</strong> Medical history, diagnoses, treatment details, prescriptions, and other health-related information necessary for your care, as governed by HIPAA (Health Insurance Portability and Accountability Act).</li>
                <li><strong>SMS Consent &amp; Communication Data:</strong> Your mobile phone number and consent status when you opt in to receive SMS messages from our patient communication program ("Clarendon Family Medicine Patient Communication").</li>
                <li><strong>Website Usage Data:</strong> Information such as browser type, IP address, pages visited, and referring URLs, collected automatically through standard web analytics tools.</li>
                <li><strong>Appointment &amp; Billing Information:</strong> Scheduling records, insurance details, and billing information necessary for administering your care.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>3. How We Use Your Information</h2>
              <p>We use the information we collect for the following purposes:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2 text-sky-700">
                <li><strong>Providing Medical Care:</strong> To deliver, coordinate, and improve your healthcare services.</li>
                <li><strong>Patient Communication:</strong> To send appointment reminders, health notifications, care instructions, and other clinically relevant messages via phone, mail, or SMS through our patient communication program.</li>
                <li><strong>Administrative Purposes:</strong> For billing, insurance claims, scheduling, and compliance with legal and regulatory obligations.</li>
                <li><strong>Website Improvement:</strong> To analyze website usage and improve our online presence.</li>
                <li><strong>Legal Compliance:</strong> To meet our obligations under applicable laws, including HIPAA, Texas state health laws, and telecommunications regulations.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>4. SMS Messaging Program — Patient Communication</h2>
              <p>
                Clarendon Family Medicine operates the <strong>"Clarendon Family Medicine Patient Communication"</strong> SMS messaging program to send patients important health-related notifications, appointment reminders, and clinic updates via text message.
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2 text-sky-700">
                <li><strong>Opt-In:</strong> You consent to receive SMS messages by signing a written consent form at our clinic. We will never enroll you without your explicit prior consent.</li>
                <li><strong>Opt-Out:</strong> You may opt out of receiving SMS messages at any time by texting <strong>STOP</strong> to our SMS number, or by notifying our staff in person or by phone. Upon receiving your opt-out request, you will receive one final confirmation message and no further messages will be sent.</li>
                <li><strong>Message Frequency:</strong> Message frequency varies. You may receive messages regarding appointment reminders, care follow-ups, health alerts, and program updates.</li>
                <li><strong>Message &amp; Data Rates:</strong> Message and data rates may apply depending on your mobile carrier and plan.</li>
                <li><strong>Help:</strong> Text <strong>HELP</strong> at any time to receive support information, or contact us directly at (806) 874-3531.</li>
              </ul>
              <p className="mt-3">
                <strong>SMS information is not shared with third parties or used for marketing purposes.</strong> Your consent to receive SMS messages is entirely separate from your clinical consent for treatment.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>5. How We Share Your Information</h2>
              <p>
                We do <strong>not</strong> sell, rent, trade, or otherwise share your personal information with third parties for marketing or commercial purposes. We may share your information only in the following limited circumstances:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2 text-sky-700">
                <li><strong>Healthcare Operations:</strong> With other treating providers, specialists, labs, and healthcare entities involved in your care, as permitted by HIPAA.</li>
                <li><strong>Insurance &amp; Billing:</strong> With your health insurance company or billing partners for claims processing and payment.</li>
                <li><strong>Legal Requirements:</strong> When required by law, regulation, court order, or governmental authority.</li>
                <li><strong>Business Associates:</strong> With service providers who assist us in operating our practice (e.g., electronic health record vendors, SMS communication platforms), under strict confidentiality agreements and HIPAA Business Associate Agreements where applicable.</li>
              </ul>
              <p className="mt-3">
                <strong>No mobile information (including SMS opt-in data and consent records) will be shared with third parties or affiliates for marketing or promotional purposes.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>6. Data Security</h2>
              <p>
                We implement administrative, physical, and technical safeguards to protect your personal and health information from unauthorized access, use, or disclosure. These measures include secure data storage, access controls, and staff training on privacy practices. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>7. HIPAA Notice</h2>
              <p>
                As a healthcare provider, Clarendon Family Medicine is required to comply with the Health Insurance Portability and Accountability Act (HIPAA). Your protected health information (PHI) is handled in accordance with our HIPAA Notice of Privacy Practices, which is provided to you at your first visit and available upon request. This Privacy Policy supplements but does not replace our HIPAA Notice of Privacy Practices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>8. Children's Privacy</h2>
              <p>
                We provide care for patients of all ages, including minors. Health information for patients under 18 is managed in accordance with HIPAA and applicable Texas laws governing parental rights and minor consent to treatment.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>9. Your Rights</h2>
              <p>Depending on applicable law and HIPAA regulations, you have the right to:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2 text-sky-700">
                <li>Access and receive a copy of your medical records and personal information</li>
                <li>Request corrections to inaccurate or incomplete information</li>
                <li>Request restrictions on how your information is used or disclosed</li>
                <li>Opt out of SMS communications at any time by texting <strong>STOP</strong></li>
                <li>File a complaint with the U.S. Department of Health &amp; Human Services if you believe your privacy rights have been violated</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>10. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically. Continued use of our services after changes are posted constitutes your acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>11. Contact Us</h2>
              <p>If you have questions, concerns, or requests regarding this Privacy Policy, please contact us:</p>
              <div className="mt-4 bg-sky-50 border border-sky-100 rounded-2xl p-6">
                <p className="font-bold text-sky-900 text-lg mb-2">Clarendon Family Medicine</p>
                <p className="text-sky-700">One Medical Center Drive</p>
                <p className="text-sky-700">Clarendon, TX 79226</p>
                <p className="text-sky-700 mt-2">Phone: <a href="tel:8068743531" className="text-sky-600 font-semibold hover:underline">(806) 874-3531</a></p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
